/**
 * calculateMicro.js
 * Calcul du revenu net avant PAS pour le statut Micro-entreprise
 * Cotisations sociales réelles calculées en local via le modèle social officiel.
 */

import { calculateChargesParCategorie, getTauxTVA } from '../helpers.js';
import { evaluateMonEntreprise } from './monEntrepriseClient';

const METIER_TO_MICRO_TYPE = {
  "Commerce (achat, revente)": "vente_marchandises",
  "Bâtiment, travaux": "prestations_services_bic",
  "Création artisanale": "prestations_services_bic",
  "Production alimentaire, culinaire": "prestations_services_bic",
  "Prestation de services (conseil, bureau d'études, communication, etc.)": "prestations_services_bnc",
  "Informatique, numérique": "prestations_services_bnc",
  "Formation professionnelle": "prestations_services_bnc",
  "Culturelle, artistique": "prestations_services_bnc",
  "Agriculture, paysage": "prestations_services_bic",
  "Service à la personne": "prestations_services_bic",
  "Bien-être, soin": "prestations_services_bnc",
};

// ========== ABATTEMENTS FISCAUX ==========
const ABATTEMENTS_FISCAUX = {
  vente_marchandises: 71,
  prestations_services_bic: 50,
  prestations_services_bnc: 34,
};

// ========== SEUILS FRANCHISE EN BASE DE TVA (annuels, en HT) ==========
const SEUILS_FRANCHISE_TVA = {
  vente_marchandises: 85000,
  prestations_services_bic: 37500,
  prestations_services_bnc: 37500,
};

// ========== MAPPING MÉTIER → TYPE D'ACTIVITÉ API ==========
const METIER_TO_ACTIVITE_NATURE = {
  "Commerce (achat, revente)": "'commerciale'",
  "Bâtiment, travaux": "'artisanale'",
  "Création artisanale": "'artisanale'",
  "Production alimentaire, culinaire": "'artisanale'",
  "Prestation de services (conseil, bureau d'études, communication, etc.)": "'libérale'",
  "Informatique, numérique": "'libérale'",
  "Formation professionnelle": "'libérale'",
  "Culturelle, artistique": "'libérale'",
  "Agriculture, paysage": "'artisanale'",
  "Service à la personne": "'libérale'",
  "Bien-être, soin": "'libérale'",
};

/**
 * Calcule le revenu net avant PAS pour le statut Micro-entreprise
 *
 * IMPORTANT: En micro-entreprise, les charges professionnelles ne sont PAS déductibles.
 * Les cotisations sociales se calculent sur le CA TTC via l'API mon-entreprise.
 *
 * @param {number} ca_ht - Chiffre d'affaires HT mensuel
 * @param {string} metier - Métier exercé
 * @param {object} charges - Charges professionnelles (NON DÉDUCTIBLES fiscalement)
 * @returns {Promise<object>} Détails complets du calcul
 */
export async function calculateMicroEntreprise(ca_ht, isTTC, metier, charges = {}, configMetiers) {
  // ========== VALIDATION ==========
  if (!ca_ht || ca_ht < 0) {
    throw new Error('Le chiffre d\'affaires doit être un nombre positif');
  }
  if (!metier || typeof metier !== 'string') {
    throw new Error('Le métier doit être spécifié');
  }

  // ========== ÉTAPE 1 : CA TTC ==========
  const taux_tva = getTauxTVA(metier, configMetiers);
  const ca_ttc = isTTC ? ca_ht : ca_ht * (1 + taux_tva);

  // ========== ÉTAPE 2 : TYPE D'ACTIVITÉ ==========
  const type_activite = METIER_TO_MICRO_TYPE[metier] || 'prestations_services_bnc';
  const abattement_fiscal = ABATTEMENTS_FISCAUX[type_activite] ?? 34;

  // ========== CONTRÔLE FRANCHISE EN BASE DE TVA ==========
  const seuil_franchise_tva = SEUILS_FRANCHISE_TVA[type_activite];
  const ca_annuel = ca_ht * 12;
  if (ca_annuel > seuil_franchise_tva) {
    return {
      incompatible: true,
      incompatibilite: {
        ca_annuel: Math.round(ca_annuel),
        seuil: seuil_franchise_tva,
        depassement: Math.round(ca_annuel - seuil_franchise_tva),
        type_activite,
      },
      statut: 'Micro-entreprise',
      categories_charges: { matieres_et_sous_traitance: 0, frais_participation_coop: 0, autres_frais_structure: 0, frais_fonctionnement: 0 },
    };
  }

  // ========== ÉTAPE 3 : COTISATIONS (modèle social local) ==========
  const activite_nature = METIER_TO_ACTIVITE_NATURE[metier] || "'libérale'";
  const ca_ttc_annuel = ca_ttc * 12;

  const activiteFlags =
    activite_nature === "'libérale'"
      ? {
          "entreprise . activité . nature": "'libérale'",
          "entreprise . activité . nature . libérale . réglementée": "non"
        }
      : activite_nature === "'artisanale'"
      ? {
          "entreprise . activité . nature": "'artisanale'"
        }
      : activite_nature === "'commerciale'"
      ? {
          "entreprise . activité . nature": "'commerciale'"
        }
      : {};

  const [revenu_net_api] = await evaluateMonEntreprise(
    ["dirigeant . auto-entrepreneur . revenu net"],
    {
      "dirigeant . auto-entrepreneur . chiffre d'affaires": {
        valeur: String(Math.round(ca_ttc_annuel)),
        unité: '€/an',
      },
      "entreprise . catégorie juridique": "'EI'",
      "entreprise . catégorie juridique . EI . auto-entrepreneur": "oui",
      ...activiteFlags
    }
  );

  // revenu_net_api est annuel → mensuel
  const revenu_net_avant_pas = revenu_net_api / 12;
  const cotisations_sociales = ca_ttc - revenu_net_avant_pas;
  const taux_cotisations = ca_ttc > 0 ? (cotisations_sociales / ca_ttc) * 100 : 0;

  // ========== ÉTAPE 5 : CHARGES PAR CATÉGORIE ==========
  const categories = calculateChargesParCategorie(charges, metier, 'MICRO', ca_ht, isTTC);
  const charges_non_deductibles =
    categories.matieres_et_sous_traitance +
    categories.autres_frais_structure +
    categories.frais_fonctionnement;

  // ========== ÉTAPE 6 : REVENU RÉEL DISPONIBLE ==========
  const revenu_reel_disponible = revenu_net_avant_pas - charges_non_deductibles;

  // ========== BASE IMPOSABLE ==========
  const base_imposable = ca_ht * (1 - abattement_fiscal / 100);

  const arrondir = (n) => Math.round(n);

  const avertissement = charges_non_deductibles > 0
    ? `⚠️ ATTENTION: En micro-entreprise, vos charges professionnelles (${arrondir(charges_non_deductibles)} €) ne sont PAS déductibles fiscalement et restent donc à votre charge personnelle (un abattement fofaitaire s'applique selon votre activité). Votre revenu réellement disponible est estimé à ${arrondir(revenu_reel_disponible)} €/mois.`
    : "En micro-entreprise, les charges professionnelles ne sont pas déductibles fiscalement.";

  return {
    source: 'api',
    revenu_net_avant_pas: arrondir(revenu_net_avant_pas),
    revenu_reel_disponible: arrondir(revenu_reel_disponible),

    ca_ht: arrondir(ca_ht),
    ca_ttc: arrondir(ca_ttc),

    metier,
    type_activite,
    taux_tva: taux_tva * 100,
    taux_cotisations,
    abattement_fiscal,

    cotisations_sociales: arrondir(cotisations_sociales),
    charges_non_deductibles: arrondir(charges_non_deductibles),

    categories_charges: {
      matieres_et_sous_traitance: arrondir(categories.matieres_et_sous_traitance),
      frais_participation_coop: 0,
      autres_frais_structure: arrondir(categories.autres_frais_structure),
      frais_fonctionnement: arrondir(categories.frais_fonctionnement),
    },

    base_imposable: arrondir(base_imposable),
    avertissement,

    statut: 'Micro-entreprise',
    description: 'Auto-entrepreneur avec régime simplifié',

    protection_sociale: {
      assurance_chomage: false,
      retraite_complete: false,
      retraite_base_limitee: true,
      accident_travail: false,
      maladie_professionnelle: false,
      formation_professionnelle: true,
    },

    avantages: [
      "Formalités simplifiées",
      "Comptabilité allégée",
      "Pas de TVA jusqu'aux seuils",
    ],
    inconvenients: [
      "Charges professionnelles non déductibles",
      "Protection sociale limitée",
      "Pas de chômage",
      "Plafonds de CA",
    ],
  };
}

export default calculateMicroEntreprise;
